import React, { useMemo, useState } from 'react';
import { Mail, Minus, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCutierStore } from '../../store/useCutierStore';
import {
  deleteLeadsById,
  fetchLeadsByMonth,
  resendCutierInviteMail,
  setLeadOrderCount,
  updateCutierLeadContact,
  verifyCutierDeletePin,
  verifyCutierEditPin,
} from '../../services/cutierFirebaseService';
import { sumLeadUnits } from '../../utils/cutier/cutierLeadUtils';

const formatDate = (iso: string): string => {
  const raw = String(iso || '').split('T')[0];
  if (!raw) return '—';
  const [y, m, d] = raw.split('-');
  if (!y || !m || !d) return raw;
  return `${d}.${m}.${y}`;
};

const CutierLeadTable: React.FC = () => {
  const selectedMonth = useCutierStore((s) => s.selectedMonth);
  const leads = useCutierStore((s) => s.leads);
  const isLoading = useCutierStore((s) => s.isLoading);
  const error = useCutierStore((s) => s.error);
  const setLeads = useCutierStore((s) => s.setLeads);

  const [selected, setSelected] = useState<string[]>([]);
  const [deletePin, setDeletePin] = useState('');
  const [busy, setBusy] = useState(false);
  const [qtyBusyId, setQtyBusyId] = useState<string | null>(null);
  const [resendBusyId, setResendBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [editLeadId, setEditLeadId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editPin, setEditPin] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const totalUnits = useMemo(() => sumLeadUnits(leads), [leads]);
  const allIds = useMemo(() => leads.map((l) => l.id), [leads]);
  const allSelected = allIds.length > 0 && selected.length === allIds.length;

  const openEdit = (leadId: string) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;
    setEditLeadId(leadId);
    setEditEmail(lead.email);
    setEditName(lead.name?.trim() || '');
    setEditPin('');
    setFeedback(null);
  };

  const closeEdit = () => {
    setEditLeadId(null);
    setEditEmail('');
    setEditName('');
    setEditPin('');
  };

  const replaceLeadInList = (oldId: string, saved: (typeof leads)[number]) =>
    leads
      .filter((row) => row.id !== oldId)
      .concat(saved)
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));

  const changeQuantity = async (leadId: string, nextCount: number) => {
    const units = Math.max(1, Math.min(99, nextCount));
    setQtyBusyId(leadId);
    setFeedback(null);
    try {
      await setLeadOrderCount(leadId, units);
      setLeads(leads.map((lead) => (lead.id === leadId ? { ...lead, orderCount: units } : lead)));
    } catch {
      setFeedback({ type: 'error', text: 'Anzahl konnte nicht gespeichert werden.' });
    } finally {
      setQtyBusyId(null);
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : allIds);
  };

  const handleResendInvite = async (leadId: string) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;
    if (!window.confirm(`Einladungs-Mail wirklich erneut an\n${lead.email}\nsenden?`)) {
      return;
    }
    setResendBusyId(leadId);
    setFeedback(null);
    try {
      const result = await resendCutierInviteMail({
        leadId: lead.id,
        email: lead.email,
        name: lead.name,
      });
      if (!result.ok) {
        setFeedback({ type: 'error', text: result.error || 'Erneuter Versand fehlgeschlagen.' });
        return;
      }
      setLeads(leads.map((row) => (row.id === leadId ? { ...row, invite_sent: true } : row)));
      setFeedback({
        type: 'success',
        text: `Einladung erneut eingereiht an ${lead.email}.`,
      });
    } catch {
      setFeedback({ type: 'error', text: 'Erneuter Versand fehlgeschlagen.' });
    } finally {
      setResendBusyId(null);
    }
  };

  const saveEdit = async (leadId: string, alsoResend: boolean) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;

    const nextEmail = editEmail.trim().toLowerCase();
    const nextName = editName.trim();
    if (!nextEmail) {
      setFeedback({ type: 'error', text: 'E-Mail darf nicht leer sein.' });
      return;
    }
    if (!editPin.trim()) {
      setFeedback({ type: 'error', text: 'Passwort erforderlich.' });
      return;
    }

    setEditBusy(true);
    setFeedback(null);
    try {
      const pinOk = await verifyCutierEditPin(editPin);
      if (!pinOk) {
        setFeedback({ type: 'error', text: 'Passwort ungültig.' });
        return;
      }

      const result = await updateCutierLeadContact({
        leadId: lead.id,
        monthKey: lead.monthKey || selectedMonth,
        email: nextEmail,
        name: nextName,
      });
      if (!result.ok || !result.lead) {
        setFeedback({ type: 'error', text: result.error || 'Speichern fehlgeschlagen.' });
        return;
      }

      const saved = result.lead;
      let nextList = replaceLeadInList(leadId, saved);
      setSelected((prev) => prev.map((id) => (id === leadId ? saved.id : id)));
      closeEdit();

      if (alsoResend) {
        const mail = await resendCutierInviteMail({
          leadId: saved.id,
          email: saved.email,
          name: saved.name,
        });
        if (!mail.ok) {
          setLeads(nextList);
          setFeedback({
            type: 'error',
            text: `Gespeichert, aber Versand fehlgeschlagen: ${mail.error || 'unbekannt'}`,
          });
          return;
        }
        nextList = replaceLeadInList(leadId, { ...saved, invite_sent: true });
        setLeads(nextList);
        setFeedback({
          type: 'success',
          text: `Korrigiert und Einladung erneut an ${saved.email} eingereiht.`,
        });
      } else {
        setLeads(nextList);
        setFeedback({
          type: 'success',
          text: `Teilnehmer korrigiert (${saved.email}).`,
        });
      }
    } catch {
      setFeedback({ type: 'error', text: 'Speichern fehlgeschlagen.' });
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.length === 0) return;
    setBusy(true);
    setFeedback(null);
    try {
      const ok = await verifyCutierDeletePin(deletePin);
      if (!ok) {
        setFeedback({ type: 'error', text: 'Lösch-Passwort ungültig.' });
        return;
      }
      const result = await deleteLeadsById(selected);
      const rows = await fetchLeadsByMonth(selectedMonth);
      setLeads(rows);
      setSelected([]);
      setDeletePin('');
      if (result.failedCount > 0 && result.deletedCount === 0) {
        setFeedback({
          type: 'error',
          text: 'Löschen blockiert (Firestore-Rules). Die Oberfläche ist lokal, Delete braucht ein Rules-Deploy.',
        });
      } else if (result.failedCount > 0) {
        setFeedback({
          type: 'error',
          text: `${result.deletedCount} gelöscht, ${result.failedCount} fehlgeschlagen.`,
        });
      } else {
        setFeedback({
          type: 'success',
          text: `${result.deletedCount} Teilnehmer entfernt. Abrechnungssumme ist aktualisiert.`,
        });
      }
    } catch {
      setFeedback({ type: 'error', text: 'Löschen fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-black uppercase tracking-wider text-pe-accent-text">
            Teilnehmerliste
          </h2>
          <p className="mt-1 text-xs text-pe-muted">
            {isLoading
              ? 'Lade Einträge…'
              : error
                ? error
                : `${leads.length} Kunden · ${totalUnits} Bestellungen im gewählten Monat`}
          </p>
        </div>
        <form onSubmit={handleDelete} className="flex flex-wrap items-center gap-2">
          <input
            type="password"
            value={deletePin}
            onChange={(e) => setDeletePin(e.target.value)}
            placeholder="Lösch-PIN oder Master"
            autoComplete="off"
            className="rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || selected.length === 0 || !deletePin.trim()}
            className="inline-flex items-center gap-2 rounded-xl border border-red-800 bg-[#1a0a0a] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-red-300 hover:text-red-200 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
            {busy ? 'Lösche…' : `Auswahl löschen (${selected.length})`}
          </button>
        </form>
      </div>

      {feedback ? (
        <div
          className={
            feedback.type === 'success'
              ? 'rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-400'
              : 'rounded-lg border border-red-800 bg-red-950/40 p-3 text-sm text-red-300'
          }
        >
          {feedback.text}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-pe-border-strong">
        <table className="w-full min-w-[780px] text-left text-sm">
          <thead className="bg-pe-sunken text-[10px] font-black uppercase tracking-widest text-pe-muted">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={leads.length === 0}
                  aria-label="Alle auswählen"
                />
              </th>
              <th className="px-3 py-2">E-Mail</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Scheren</th>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Quelle</th>
              <th className="px-3 py-2">Mail-Status</th>
              <th className="px-3 py-2">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-pe-muted">
                  Keine Teilnehmer in diesem Monat.
                </td>
              </tr>
            ) : (
              leads.map((lead) => (
                <React.Fragment key={lead.id}>
                  <tr className="border-t border-pe-border-strong hover:bg-pe-hover">
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selected.includes(lead.id)}
                        onChange={() => toggleOne(lead.id)}
                        aria-label={lead.email}
                      />
                    </td>
                    <td className="px-3 py-2 align-top break-all font-medium text-pe-text">{lead.email}</td>
                    <td className="px-3 py-2 align-top text-pe-muted">{lead.name?.trim() || '—'}</td>
                    <td className="px-3 py-2 align-top">
                      <div className="inline-flex items-center gap-1 rounded-lg border border-pe-border-strong bg-pe-sunken">
                        <button
                          type="button"
                          disabled={qtyBusyId === lead.id || lead.orderCount <= 1}
                          onClick={() => void changeQuantity(lead.id, lead.orderCount - 1)}
                          className="px-2 py-1 text-pe-muted hover:text-pe-text disabled:opacity-30"
                          aria-label="Eine Schere weniger"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-[2rem] text-center text-sm font-black text-pe-accent-text">
                          {lead.orderCount}×
                        </span>
                        <button
                          type="button"
                          disabled={qtyBusyId === lead.id}
                          onClick={() => void changeQuantity(lead.id, lead.orderCount + 1)}
                          className="px-2 py-1 text-pe-accent-text hover:text-white disabled:opacity-30"
                          aria-label="Eine Schere mehr"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap text-pe-muted">
                      {formatDate(lead.createdAt)}
                    </td>
                    <td className="px-3 py-2 align-top text-pe-muted">
                      {lead.source === 'bulk' ? 'Bulk' : 'Einzel'}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={
                          lead.invite_sent
                            ? 'rounded-full border border-[#E6C299]/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-pe-accent-text'
                            : 'rounded-full border border-pe-border-strong px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-pe-muted'
                        }
                      >
                        {lead.invite_sent ? 'Mail gesendet' : 'Offen'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => (editLeadId === lead.id ? closeEdit() : openEdit(lead.id))}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-pe-border-strong bg-pe-sunken px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wider text-pe-muted hover:text-pe-text"
                          title="E-Mail / Name korrigieren"
                        >
                          {editLeadId === lead.id ? (
                            <X className="h-3.5 w-3.5" />
                          ) : (
                            <Pencil className="h-3.5 w-3.5" />
                          )}
                          {editLeadId === lead.id ? 'Schließen' : 'Bearbeiten'}
                        </button>
                        <button
                          type="button"
                          disabled={resendBusyId === lead.id}
                          onClick={() => void handleResendInvite(lead.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wider text-pe-accent-text hover:bg-[#E6C299]/20 disabled:opacity-40"
                          title="Einladungs-Mail erneut an diese Adresse senden"
                        >
                          <Mail className="h-3.5 w-3.5" />
                          {resendBusyId === lead.id ? 'Sende…' : 'Nochmals senden'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {editLeadId === lead.id ? (
                    <tr className="border-t border-pe-border-strong bg-pe-sunken/60">
                      <td colSpan={8} className="px-3 py-3">
                        <EditLeadForm
                          email={editEmail}
                          name={editName}
                          pin={editPin}
                          busy={editBusy}
                          onEmailChange={setEditEmail}
                          onNameChange={setEditName}
                          onPinChange={setEditPin}
                          onCancel={closeEdit}
                          onSave={(alsoResend) => void saveEdit(lead.id, alsoResend)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EditLeadForm: React.FC<{
  email: string;
  name: string;
  pin: string;
  busy: boolean;
  onEmailChange: (v: string) => void;
  onNameChange: (v: string) => void;
  onPinChange: (v: string) => void;
  onCancel: () => void;
  onSave: (alsoResend: boolean) => void;
}> = ({ email, name, pin, busy, onEmailChange, onNameChange, onPinChange, onCancel, onSave }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
    <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-[10px] font-black uppercase tracking-wider text-pe-muted">
      E-Mail
      <input
        type="email"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        autoComplete="off"
        className="rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-sm font-normal normal-case tracking-normal text-pe-text focus:border-[#E6C299] focus:outline-none"
      />
    </label>
    <label className="flex min-w-[160px] flex-1 flex-col gap-1 text-[10px] font-black uppercase tracking-wider text-pe-muted">
      Name
      <input
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        autoComplete="off"
        className="rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-sm font-normal normal-case tracking-normal text-pe-text focus:border-[#E6C299] focus:outline-none"
      />
    </label>
    <label className="flex min-w-[140px] flex-col gap-1 text-[10px] font-black uppercase tracking-wider text-pe-muted">
      Passwort
      <input
        type="password"
        value={pin}
        onChange={(e) => onPinChange(e.target.value)}
        placeholder="Master oder Partner-PIN"
        autoComplete="off"
        className="rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-sm font-normal normal-case tracking-normal text-pe-text focus:border-[#E6C299] focus:outline-none"
      />
    </label>
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={busy || !pin.trim()}
        onClick={() => onSave(false)}
        className="rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2 text-[10px] font-black uppercase tracking-widest text-pe-text hover:bg-pe-hover disabled:opacity-40"
      >
        {busy ? 'Speichere…' : 'Speichern'}
      </button>
      <button
        type="button"
        disabled={busy || !pin.trim()}
        onClick={() => onSave(true)}
        className="rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-pe-accent-text hover:bg-[#E6C299]/20 disabled:opacity-40"
      >
        Speichern & senden
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onCancel}
        className="rounded-xl border border-transparent px-3 py-2 text-[10px] font-black uppercase tracking-widest text-pe-muted hover:text-pe-text"
      >
        Abbrechen
      </button>
    </div>
  </div>
);

export default CutierLeadTable;
